/**
 * Where a share card lives, as a URL the browser can build.
 *
 * ## Why this is here and not in `og/`
 *
 * The key shape already exists in `og/src/card-keys.ts`, and the tiny-URL modal
 * needs the same URL to preview the card it is about to share. Two copies of a
 * key shape is the failure `card-keys.ts` itself warns about — a key built one
 * character differently is an object that exists, is well-formed, and is
 * invisible to the thing meant to serve it. Here that would be a preview of a
 * card nobody wrote.
 *
 * So the shape lives once, in `site/`, and `og/` imports it — the direction
 * every other shared module already runs (`card-map.ts`, `palette.ts`,
 * `url-state.ts`). A site-importing-og edge would be a reference cycle.
 *
 * ## What is *not* shared, and why that is fine
 *
 * The part segment is a sha256 prefix, and the two runtimes cannot hash the
 * same way: Node has synchronous `createHash`, the browser has asynchronous
 * `crypto.subtle.digest`. Sharing a hash function would mean making the Node
 * path async for no reason, or shipping a hand-rolled sha256 to both.
 *
 * So each side hashes with its own primitive and both hand the result to the
 * same builder. The part that can drift — the layout of the key — is single
 * -sourced; the part that cannot drift, because it is a published digest with a
 * fixed answer, is computed twice. `site/test/card-url.test.ts` pins a vector
 * that both must produce.
 */

import { RENDERER_VERSION } from "./renderer-version.js";

/** The card distribution. Card *images* are the only thing it serves. */
export const CARD_ORIGIN = "https://og.tensorlens.dev";

export const CARD_PREFIX = `cards/${RENDERER_VERSION}/`;
export const LATEST_PREFIX = `latest/${RENDERER_VERSION}/`;
export const FALLBACK_KEY = `fallback/${RENDERER_VERSION}/default.png`;

/** Characters of the part digest that reach the key. */
export const PART_SEGMENT_LENGTH = 12;
/**
 * Longest part label accepted.
 *
 * About the digest rather than the path: the value is hashed before it reaches
 * either, so no charset rule would protect anything and a label legitimately
 * contains slashes and dots. What a cap prevents is an unauthenticated request
 * asking for a megabyte of hashing.
 */
export const MAX_PART_LENGTH = 256;

/**
 * The key for a card, given an already-hashed part segment.
 *
 * `partHash` rather than `part`, and that is the whole point of the split: this
 * function is pure string assembly and runs identically in both runtimes, while
 * the hashing that cannot be shared happens in the caller.
 *
 * The part segment sits *above* the sha so one part's revisions stay listable
 * as a prefix, and so a repo that never had parts keeps the key it already had.
 */
export function cardKeyFromHash(
  repoId: string,
  sha: string,
  partHash?: string,
): string {
  const scope = partHash === undefined ? "" : `${partHash}/`;
  return `${CARD_PREFIX}${repoId}/${scope}${sha}.png`;
}

/** The mutable alias, for a view with no pinned revision. */
export function latestKeyFromHash(repoId: string, partHash?: string): string {
  return partHash === undefined
    ? `${LATEST_PREFIX}${repoId}.png`
    : `${LATEST_PREFIX}${repoId}/${partHash}.png`;
}

/**
 * The part segment, hashed in the browser.
 *
 * Async because `crypto.subtle` is. The Node counterpart in
 * `og/src/card-keys.ts` is synchronous and must produce the same twelve
 * characters — pinned by a shared vector rather than by inspection.
 */
export async function partSegmentInBrowser(part: string): Promise<string> {
  if (part === "") throw new Error("refusing to build a key for an empty part");
  if (part.length > MAX_PART_LENGTH) {
    throw new Error("refusing to build a key for an implausibly long part");
  }
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(part));
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, PART_SEGMENT_LENGTH);
}

/**
 * The card URL for a view, or the generic fallback when there is no model.
 *
 * A pinned revision gets the immutable key; a rev-less view gets the alias.
 * That mirrors `ogTagsFor` exactly, because it is the same decision — and the
 * modal's preview should show what a crawler would fetch, not an approximation
 * of it.
 */
export async function cardUrlFor(
  repoId: string | undefined,
  rev: string | undefined,
  part: string | undefined,
): Promise<string> {
  if (repoId === undefined) return `${CARD_ORIGIN}/${FALLBACK_KEY}`;
  const partHash =
    part === undefined || part === "" ? undefined : await partSegmentInBrowser(part);
  const key =
    rev === undefined || rev === ""
      ? latestKeyFromHash(repoId, partHash)
      : cardKeyFromHash(repoId, rev, partHash);
  return `${CARD_ORIGIN}/${key}`;
}
